import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, Loader2 } from "lucide-react";

const VARS = ["{student_name}", "{school_name}", "{grade}", "{class}", "{date}"];
const empty = { id: null, title: "", badge: "مخصص", body: "" };

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setItems(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const builtins = items.filter((t) => t.builtin && t.id !== "custom");
  const customs = items.filter((t) => !t.builtin);

  const openAdd = () => { setForm(empty); setDialogOpen(true); };
  const openEdit = (t) => { setForm({ id: t.id, title: t.title, badge: t.badge, body: t.body }); setDialogOpen(true); };
  const insertVar = (v) => setForm((f) => ({ ...f, body: (f.body ? f.body + " " : "") + v }));

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("يرجى تعبئة العنوان والنص"); return; }
    setSaving(true);
    try {
      const payload = { title: form.title, badge: form.badge || "مخصص", body: form.body };
      if (form.id) await api.put(`/templates/${form.id}`, payload);
      else await api.post("/templates", payload);
      toast.success(form.id ? "تم تعديل القالب" : "تمت إضافة القالب");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      toast.success("تم حذف القالب");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">القوالب</h1>
          <p className="text-slate-400">قوالب جاهزة للاستخدام، ويمكنك إضافة قوالبك الخاصة وتعديلها</p>
        </div>
        <Button onClick={openAdd} className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="add-template-button">
          <Plus className="h-4 w-4" /> إضافة قالب
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-bold text-slate-500">
              <Lock className="h-4 w-4" /> قوالب جاهزة (غير قابلة للتعديل)
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {builtins.map((t) => (
                <Card key={t.id} data-testid={`builtin-template-${t.id}`}>
                  <CardContent className="space-y-2 p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800">{t.title}</span>
                      <Badge className="bg-slate-100 text-slate-600">{t.badge}</Badge>
                    </div>
                    <p className="text-xs leading-relaxed text-slate-500">{t.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-bold text-slate-500">قوالبي المخصصة</h2>
            {customs.length === 0 ? (
              <p className="rounded-lg bg-slate-50 py-6 text-center text-sm text-slate-400">
                لا توجد قوالب مخصصة بعد. أضف قالبك الأول بالضغط على «إضافة قالب».
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {customs.map((t) => (
                  <Card key={t.id} data-testid={`custom-template-${t.id}`}>
                    <CardContent className="space-y-2 p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-800">{t.title}</span>
                        <div className="flex items-center gap-1">
                          <Badge className="bg-emerald-100 text-emerald-700">{t.badge}</Badge>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                            <Pencil className="h-4 w-4 text-emerald-700" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`delete-template-${t.id}`}>
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="text-right">
                              <AlertDialogHeader>
                                <AlertDialogTitle>حذف القالب</AlertDialogTitle>
                                <AlertDialogDescription>هل تريد حذف قالب «{t.title}»؟</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>إلغاء</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove(t.id)} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                      <p className="text-xs leading-relaxed text-slate-500">{t.body}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="text-right">
          <DialogHeader><DialogTitle>{form.id ? "تعديل القالب" : "إضافة قالب جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs text-slate-500">عنوان القالب *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="template-title" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">التصنيف (وسم قصير)</Label>
              <Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="template-badge" placeholder="مثال: تنبيه" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">نص الرسالة *</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} data-testid="template-body" />
              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="text-xs text-slate-400">إدراج متغير:</span>
                {VARS.map((v) => (
                  <button key={v} onClick={() => insertVar(v)} type="button" data-testid={`insert-var-${v}`}
                    className="rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-xs text-emerald-700 hover:bg-emerald-100">
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="save-template-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
